// AICA Sparkline Component
// Small inline charts for showing trends
// Bloomberg Terminal style: Minimal, informative, contextual

import { memo } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SparklineDataPoint {
  value: number;
  label?: string;
}

export interface SparklineProps {
  /**
   * Chart data
   */
  data: SparklineDataPoint[] | number[];
  /**
   * Chart type
   */
  type?: 'line' | 'area' | 'bar';
  /**
   * Color (uses design tokens)
   */
  color?: 'primary' | 'success' | 'danger' | 'warning' | 'info';
  /**
   * Width in pixels
   */
  width?: number;
  /**
   * Height in pixels
   */
  height?: number;
  /**
   * Show trend indicator
   */
  showTrend?: boolean;
  /**
   * Additional className
   */
  className?: string;
}

/**
 * Sparkline Chart Component
 * Small inline chart for showing trends at a glance
 *
 * @example
 * ```tsx
 * <Sparkline
 *   data={[4, 5, 6, 7, 8, 9, 10]}
 *   type="line"
 *   color="primary"
 *   showTrend
 * />
 * ```
 */
export const Sparkline = memo(function Sparkline({
  data,
  type = 'line',
  color = 'primary',
  width = 100,
  height = 30,
  showTrend = false,
  className,
}: SparklineProps) {
  // Normalize data to array of objects
  const chartData = (
    Array.isArray(data) && typeof data[0] === 'number'
      ? (data as number[]).map((value, index) => ({ value, label: index.toString() }))
      : data
  ) as SparklineDataPoint[];

  // Calculate trend
  const lastPoint = chartData[chartData.length - 1];
  const firstPoint = chartData[0];
  const trend =
    chartData.length >= 2 && lastPoint && firstPoint
      ? lastPoint.value - firstPoint.value
      : 0;

  const trendDirection = trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat';

  // Color mapping - uses CSS custom properties from theme
  const colorMap = {
    primary: 'var(--color-primary)',
    success: 'var(--color-success)',
    danger: 'var(--color-danger)',
    warning: 'var(--color-warning)',
    info: 'var(--color-info)',
  };

  const strokeColor = colorMap[color];
  // Use dim variant CSS variable for fill (12% opacity defined in theme)
  const fillColorMap = {
    primary: 'var(--color-primary-dim)',
    success: 'var(--color-success-dim)',
    danger: 'var(--color-danger-dim)',
    warning: 'var(--color-warning-dim)',
    info: 'var(--color-info-dim)',
  };
  const fillColor = fillColorMap[color];

  return (
    <div
      data-testid="sparkline"
      className={cn('inline-flex items-center gap-2', className)}
    >
      <ResponsiveContainer data-testid="sparkline-chart" width={width} height={height}>
        {type === 'line' && (
          <LineChart data={chartData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={strokeColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        )}
        {type === 'area' && (
          <AreaChart data={chartData}>
            <Area
              type="monotone"
              dataKey="value"
              stroke={strokeColor}
              strokeWidth={2}
              fill={fillColor}
              isAnimationActive={false}
            />
          </AreaChart>
        )}
        {type === 'bar' && (
          <BarChart data={chartData}>
            <Bar dataKey="value" fill={strokeColor} isAnimationActive={false} />
          </BarChart>
        )}
      </ResponsiveContainer>

      {showTrend && (
        <span data-testid="sparkline-trend" className="flex items-center">
          {trendDirection === 'up' && <TrendingUp className="h-4 w-4 text-success" />}
          {trendDirection === 'down' && <TrendingDown className="h-4 w-4 text-danger" />}
          {trendDirection === 'flat' && <Minus className="h-4 w-4 text-fg-muted" />}
        </span>
      )}
    </div>
  );
});

/**
 * Sparkline with Value Display
 * Shows the chart with the current value
 *
 * @example
 * ```tsx
 * <SparklineWithValue
 *   data={[4, 5, 6, 7, 8.5]}
 *   label="Avg Score"
 *   format={(v) => v.toFixed(1)}
 * />
 * ```
 */
export interface SparklineWithValueProps extends Omit<SparklineProps, 'width'> {
  /**
   * Label text
   */
  label?: string;
  /**
   * Value formatter
   */
  format?: (value: number) => string;
  /**
   * Show change percentage
   */
  showChange?: boolean;
}

export const SparklineWithValue = memo(function SparklineWithValue({
  data,
  label,
  format = (v) => v.toString(),
  showChange = false,
  className,
  ...sparklineProps
}: SparklineWithValueProps) {
  // Normalize data
  const chartData = (
    Array.isArray(data) && typeof data[0] === 'number'
      ? (data as number[]).map((value) => ({ value }))
      : data
  ) as SparklineDataPoint[];

  const currentValue = chartData[chartData.length - 1]?.value || 0;
  const previousValue = chartData[0]?.value || 0;
  const change = chartData.length >= 2 ? currentValue - previousValue : 0;
  const changePercent =
    previousValue !== 0 ? ((change / previousValue) * 100).toFixed(1) : '0';

  return (
    <div
      data-testid="sparkline-with-value"
      className={cn('flex items-center justify-between gap-4', className)}
    >
      <div className="flex-1 min-w-0">
        {label && (
          <p data-testid="sparkline-label" className="text-sm text-fg-muted mb-1">
            {label}
          </p>
        )}
        <div className="flex items-baseline gap-2">
          <span
            data-testid="sparkline-value"
            className="text-2xl font-semibold text-fg-secondary"
          >
            {format(currentValue)}
          </span>
          {showChange && change !== 0 && (
            <span
              data-testid="sparkline-change"
              className={cn(
                'text-xs font-medium',
                change > 0 ? 'text-success' : 'text-danger'
              )}
            >
              {change > 0 ? '+' : ''}
              {changePercent}%
            </span>
          )}
        </div>
      </div>
      <Sparkline data={chartData} width={80} height={40} {...sparklineProps} />
    </div>
  );
});
